import { useEffect, useMemo, useState } from "react";
import { GlassCard, Button, StatusPill, useToast } from "@/shared/ui";
import { useCryptoSubmit, useOrderStatus } from "@/shared/api/hooks";
import { useI18n } from "@/shared/i18n";
import { ApiError } from "@/shared/api/client";
import type { CreateOrderResponse } from "@/shared/api/schemas";

/**
 * USDC/USDT are 6-decimal; the backend sends min-units as a decimal string.
 * Format by string arithmetic (no Number) so a large min-unit value can never
 * lose precision past 2^53.
 */
function formatStable(amount?: string, decimals = 6, dp = 2): string {
  if (!amount || !/^\d+$/.test(amount)) return "—";
  const padded = amount.padStart(decimals + 1, "0");
  const intPart = padded.slice(0, padded.length - decimals).replace(/^0+(?=\d)/, "");
  const frac = padded.slice(padded.length - decimals).slice(0, dp).padEnd(dp, "0");
  return `${intPart}.${frac}`;
}

function useCountdown(expiredLabel: string, expiresAt?: string) {
  const target = useMemo(
    () => (expiresAt ? new Date(expiresAt).getTime() : NaN),
    [expiresAt],
  );
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  if (Number.isNaN(target)) return { text: "—", expired: false };
  const ms = target - now;
  if (ms <= 0) return { text: expiredLabel, expired: true };
  const s = Math.floor(ms / 1000);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return { text: `${mm}:${ss}`, expired: false };
}

interface Props {
  order: CreateOrderResponse;
  onPaid: () => void;
  /** Dismiss the card (an unpaid order simply expires server-side). */
  onClose: () => void;
}

export function CryptoPayCard({ order, onPaid, onClose }: Props) {
  const toast = useToast();
  const { t } = useI18n();
  const submit = useCryptoSubmit();
  const status = useOrderStatus(order.out_trade_no);
  const { text: countdown, expired } = useCountdown(t("Expired"), order.expires_at);

  const [txHash, setTxHash] = useState("");
  const [pending, setPending] = useState<{ confirmations: number; needed: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Fallback: polling drives terminal state even without a submit.
  const st = status.data?.status;
  const paid = st === "paid";
  const closed = st === "failed" || st === "closed";
  const done = paid || closed; // hide the submit form once terminal either way

  async function onSubmit() {
    setErr(null);
    setPending(null);
    if (!txHash.trim()) {
      setErr(t("Enter the transfer transaction hash (tx hash)"));
      return;
    }
    try {
      const res = await submit.mutateAsync({
        out_trade_no: order.out_trade_no,
        tx_hash: txHash,
      });
      if (res.status === "paid") {
        onPaid();
      } else {
        setPending({ confirmations: res.confirmations ?? 0, needed: res.needed ?? 0 });
      }
    } catch (e) {
      setErr(e instanceof ApiError ? t(e.message) : t("Submit failed, please retry"));
    }
  }

  async function copyAddr() {
    if (!order.pay_to) return;
    try {
      await navigator.clipboard.writeText(order.pay_to);
      toast.success(t("Pay-to address copied"));
    } catch {
      toast.error(t("Copy failed — select the address manually"));
    }
  }

  return (
    <GlassCard
      title={t("Stablecoin payment")}
      action={
        paid ? (
          <StatusPill tone="ok" dot>
            {t("Received")}
          </StatusPill>
        ) : closed ? (
          <StatusPill tone="error" dot>
            {t("Closed")}
          </StatusPill>
        ) : (
          <StatusPill tone="pending" dot>
            {t("Awaiting payment")}
          </StatusPill>
        )
      }
    >
      <div className="pay-rows">
        <div className="pay-row">
          <span className="pay-row__k">{t("Amount due")}</span>
          <span className="pay-row__v">
            <span className="pay-amount">
              {formatStable(order.amount)} <small>{t("USDC/USDT (stablecoin)")}</small>
            </span>
          </span>
        </div>
        <div className="pay-row">
          <span className="pay-row__k">{t("Chain")}</span>
          <span className="pay-row__v">{order.chain ?? "—"}</span>
        </div>
        <div className="pay-row">
          <span className="pay-row__k">{t("Pay-to address")}</span>
          <span className="pay-row__v">
            <span>{order.pay_to ?? "—"}</span>
            <button className="copy-btn" onClick={copyAddr} type="button">
              {t("Copy")}
            </button>
          </span>
        </div>
        <div className="pay-row">
          <span className="pay-row__k">{t("Token")}</span>
          <span className="pay-row__v contract">{order.token ?? "—"}</span>
        </div>
        <div className="pay-row">
          <span className="pay-row__k">{t("Time left")}</span>
          <span className="pay-row__v">
            <span className={`countdown ${expired ? "countdown--expired" : "countdown--live"}`}>
              {countdown}
            </span>
          </span>
        </div>
      </div>

      <div className="warn-box">
        ⚠{" "}
        {t(
          "Send the exact amount to the address above on {chain} ONLY; a wrong chain / amount / address cannot be recovered.",
          { chain: order.chain ?? t("the specified") },
        )}
      </div>

      {!done && (
        <>
          <div className="submit-row">
            <input
              className="input mono"
              placeholder={t("Transaction hash 0x…")}
              value={txHash}
              onChange={(e) => setTxHash(e.target.value)}
            />
            <Button onClick={onSubmit} loading={submit.isPending} disabled={!txHash.trim()}>
              {t("I've sent it — verify")}
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={submit.isPending}>
              {t("Cancel")}
            </Button>
          </div>

          {expired && !err && !pending && (
            <div className="pay-note pay-note--pending">
              {t(
                "Order expired — if you already sent the payment, paste the tx hash above and it will still be credited; otherwise cancel and create a new order.",
              )}
            </div>
          )}
          {err && <div className="pay-note pay-note--err">{err}</div>}
          {pending && !err && (
            <div className="pay-note pay-note--pending">
              {t("Confirming {c}/{n} — you can resubmit the same tx later until it lands.", {
                c: pending.confirmations,
                n: pending.needed,
              })}
            </div>
          )}
        </>
      )}

      {paid && <div className="pay-note pay-note--ok">{t("Payment received — tokens credited.")}</div>}
      {closed && (
        <>
          <div className="pay-note pay-note--err">{t("Order closed — create a new order to pay.")}</div>
          <div className="submit-row">
            <Button variant="ghost" onClick={onClose}>
              {t("Back to packages")}
            </Button>
          </div>
        </>
      )}
    </GlassCard>
  );
}
